import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import styles from './Settings.scss'
import { MilestoneConfig } from '../MilestoneConfig'
import { LOAD_EXISTING_MODAL, resultTypes } from 'features/search/LoadExisting'
import { GENERAL_MODAL } from 'common/modals/GeneralModal'
import { SectionBox } from 'common/styleguide/SectionBox'
import { DatePickerInput } from 'common/styleguide/DatePickerInput'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { BlockButtonGroup } from 'common/styleguide/BlockButtonGroup'
import { InteriorLabelInput } from 'common/styleguide/InteriorLabelInput'
import { CTA } from 'common/styleguide/CTA'
import { Icon } from 'common/Icon'
import { CircledIcon } from 'common/CircledIcon'
import { Distance, Elevation } from 'common/units'
import { RouteListItem } from 'common/ListItems'
import { CircleSpinner } from 'common/spinners'
import { useFormatMessage, featureNames, commonVerbs, commonUnits, commonMetrics, commonCTAs, commonMessages } from 'lib/localization'
import { getPreferences } from 'lib/utils'
import { c } from 'lib/textUtils'
import { showModal } from 'lib/modalUtils'
import { unitMessages } from 'lib/units/unitTypes'
import { METERS_IN_MILE, METERS_IN_KM, METERS_IN_FOOT } from 'lib/units/constants'
import { routeActions, routeSelectors } from 'data/resources/routes'
import { defineMessages } from 'react-intl'
import { Map, OrderedMap } from 'immutable'
import cx from 'classnames'
import _ from 'lodash'

const allowManualOpts = fm => OrderedMap({
  true: fm(commonMessages.yes),
  false: fm(commonMessages.no)
})

const ptMap = fm => OrderedMap({
  individual: <div className={styles.ptBtn}>
    <div className={styles.btnLeft}>
      <CircledIcon size={50} ringColor='#E0E0E0' style={{ marginRight: '10px' }} i='md-person' />
    </div>
    <div className={styles.btnRight}>
      <span>{fm(messages.individual)}</span>
      <span>{fm(messages.individualExplain)}</span>
    </div>
  </div>,
  community: <div className={styles.ptBtn}>
    <div className={styles.btnLeft}>
      <CircledIcon size={50} ringColor='#E0E0E0' style={{ marginRight: '10px' }} i='md-people' />
    </div>
    <div className={styles.btnRight}>
      <span>{fm(messages.community)}</span>
      <span>{fm(messages.communityExplain)}</span>
    </div>
  </div>
})

const critMap = fm => OrderedMap({
  distance: <><Icon style={{ marginRight: '10px' }} i='distance' size={26} />{fm(commonMetrics.distance)}</>,
  elevation_gain: <><Icon style={{ marginRight: '10px' }} i='elevation-icon' size={26} />{fm(commonMetrics.elevation)}</>,
  moving_time: <><Icon style={{ marginRight: '10px' }} i='md-stopwatch' size={26} />{fm(commonMetrics.time)}</>
  // race: <><Icon style={{ marginRight: '10px' }} i='md-trophy' size={26} />{fm(messages.race)}</>
})

const needsConfirmChanges = (edits, params, qualifiers) => {
  return edits.has('startsOn') || edits.has('endsOn') ||
  params.has('participation_type') ||
  qualifiers.has('tag') || qualifiers.has('manual')
}

const milestonesBlocking = (edits, params) => {
  return edits.has('goalType') ||
  params.has('max') || params.has('has_target') || params.has('route_id')
}

const messages = defineMessages({
  starts: {
    id: 'ChallengeSettings.starts',
    defaultMessage: 'Starts'
  },
  to: {
    id: 'ChallengeSettings.to',
    defaultMessage: 'to',
    description: 'Used between two dates, for example: "01-01-2021 to 01-02-2021".'
  },
  ends: {
    id: 'ChallengeSettings.ends',
    defaultMessage: 'Ends'
  },
  tagLabel: {
    id: 'ChallengeSettings.tagLabel',
    defaultMessage: 'Only include activities with the following hashtag in the title (optional):'
  },
  tagPlaceholder: {
    id: 'ChallengeSettings.tagPlaceholder',
    defaultMessage: 'ex: #gravel'
  },
  allowManual: {
    id: 'ChallengeSettings.allowManual',
    defaultMessage: 'Allow manually created activities?'
  },
  participationType: {
    id: 'ChallengeSettings.participationType',
    defaultMessage: 'Partcipation Type'
  },
  individual: {
    id: 'ChallengeSettings.individual',
    defaultMessage: 'Individual'
  },
  individualExplain: {
    id: 'ChallengeSettings.individualExplain',
    defaultMessage: 'Participants will compete on an individual basis.'
  },
  community: {
    id: 'ChallengeSettings.community',
    defaultMessage: 'Community'
  },
  communityExplain: {
    id: 'ChallengeSettings.communityExplain',
    defaultMessage: 'Participants will work together collectively.'
  },
  challengeCriteria: {
    id: 'ChallengeSettings.challengeCriteria',
    defaultMessage: 'Challenge Criteria'
  },
  race: {
    id: 'ChallengeSettings.race',
    defaultMessage: 'Race',
    description: 'A competition'
  },
  distanceDesc: {
    id: 'ChallengeSettings.distanceDesc',
    defaultMessage: 'Challenge participants based on distance. Set a specific target (ex: ride 5000 {units} in 2021), leave it open-ended (ex: ride as many {units} as you can in 2021), or select routes from your library to set the distance (ex: select a 3235 {unit} coast-to-coast route and challenge participants to amass that distance for virtual cross-country ride). <a>Learn more</a>'
  },
  elevationDesc: {
    id: 'ChallengeSettings.elevationDesc',
    defaultMessage: 'Challenge participants to climb. You can set a target elevation gain (ex: climb 20,000{unit} in April) or leave it open-ended (ex: climb as much as you can in April). <a>Learn more</a>'
  },
  timeDesc: {
    id: 'ChallengeSettings.timeDesc',
    defaultMessage: 'Challenge participants to spend more time riding. You can set a target time (ex: ride for 500 hours in April) or leave it open-ended (ex: ride as much as you can in April). Note: Only moving time will count towards the Challenge, not total time recorded. <a>Learn more</a>'
  },
  completionTarget: {
    id: 'ChallengeSettings.completionTarget',
    defaultMessage: 'Completion Target'
  },
  setDistance: {
    id: 'ChallengeSettings.setDistance',
    defaultMessage: 'Set target distance of'
  },
  setElevation: {
    id: 'ChallengeSettings.setElevation',
    defaultMessage: 'Set target elevation of'
  },
  setTime: {
    id: 'ChallengeSettings.setTime',
    defaultMessage: 'Set target time of'
  },
  fixedDistance: {
    id: 'ChallengeSettings.fixedDistance',
    defaultMessage: 'Fixed distance'
  },
  fixedElevation: {
    id: 'ChallengeSettings.fixedElevation',
    defaultMessage: 'Fixed elevation'
  },
  fixedTime: {
    id: 'ChallengeSettings.fixedTime',
    defaultMessage: 'Fixed time'
  },
  routeDistance: {
    id: 'ChallengeSettings.routeDistance',
    defaultMessage: 'Route distance'
  },
  noTarget: {
    id: 'ChallengeSettings.noTarget',
    defaultMessage: 'No target'
  },
  saveChanges: {
    id: 'ChallengeSettings.saveChanges',
    defaultMessage: 'Save changes'
  },
  selectARoute: {
    id: 'ChallengeSettings.selectARoute',
    defaultMessage: 'Select a Route'
  },
  addToChallenge: {
    id: 'ChallengeSettings.addToChallenge',
    defaultMessage: 'Add to Challenge'
  },
  replace: {
    id: 'ChallengeSettings.replace',
    defaultMessage: 'Replace'
  },
  saveWithParticipants: {
    id: 'ChallengeSettings.saveWithParticipants',
    defaultMessage: 'This Challenge currently has participants. Altering the Challenge settings could impact the rankings on the Challenge leaderboard and take a few minutes to process. Are you sure you wish to proceed?'
  },
  confirmWithParticipants: {
    id: 'ChallengeSettings.confirmWithParticipants',
    defaultMessage: 'Yes, Save Changes'
  },
  milestonesBlocking: {
    id: 'ChallengeSettings.milestonesBlocking',
    defaultMessage: 'Altering this Challenge criteria will affect the Milestones you have created. Before making this change you will need to delete your Milestones.'
  }
})

export const Settings = ({ goal, participants, milestones, updateGoal, forceRefetch, location }) => {
  const fm = useFormatMessage()
  const { metric_units: metricUnits } = getPreferences()
  const [savePending, setSavePending] = useState(false)
  const [pendingEdits, setPendingEdits] = useState(Map())
  const [pendingParams, setPendingParams] = useState(Map())
  const [pendingQualifiers, setPendingQualifiers] = useState(Map())

  const milestonesRef = useRef(null)
  useEffect(() => {
    if (location?.hash === '#milestones') {
      milestonesRef.current && milestonesRef.current.scrollIntoView({
        behavior: 'smooth'
      })
    }
  }, [])

  const setPendingEdit = (key, value) => {
    if (goal.get(key) === value) {
      setPendingEdits(pendingEdits.delete(key))
    } else {
      setPendingEdits(pendingEdits.merge({ [key]: value }))
    }
  }
  const setParam = (...args) => {
    if (typeof args[0] === 'object') {
      const nextParams = pendingParams.withMutations(p => {
        Object.keys(args[0]).forEach(k => {
          if (goal.get('goalParams')[k] === args[0][k]) {
            p.delete(k)
          } else {
            p.set(k, args[0][k])
          }
        })
        return p
      })
      setPendingParams(nextParams)
    } else {
      const [param, value] = args
      if (goal.get('goalParams')[param] === value) {
        setPendingParams(pendingParams.delete(param))
      } else {
        setPendingParams(pendingParams.set(param, value))
      }
    }
  }
  const setQualifier = (qualifier, value) => {
    if (goal.get('goalParams')['qualifiers'][qualifier] === value) {
      setPendingQualifiers(pendingQualifiers.delete(qualifier))
    } else {
      setPendingQualifiers(pendingQualifiers.merge({ [qualifier]: value }))
    }
  }

  const values = goal.merge(pendingEdits)
  const params = Map(goal.goalParams).merge(pendingParams)
  const qualifiers = Map(goal.get('goalParams').qualifiers).merge(pendingQualifiers)

  const onTagChange = e => {
    let tag = e.target.value
    if (!qualifiers.get('tag') && e.target.value !== '#') {
      tag = '#' + tag
    }
    // only accept alphanumeric (plus _ and #) tags
    setQualifier('tag', tag.replace(/[^a-z0-9_#]/gi, ''))
  }

  const onGoalTypeChange = e => {
    if (e === 'elevation_gain' || e === 'moving_time') {
      setParam('route_id', null)
    }
    setPendingEdit('goalType', e)
  }

  const onSubmit = (force = false) => {
    if (milestones.size > 0 && milestonesBlocking(pendingEdits, pendingParams)) {
      showModal(GENERAL_MODAL, {
        component: fm(messages.milestonesBlocking)
      })
      return
    }

    if (!force && participants.size > 0 && needsConfirmChanges(pendingEdits, pendingParams, pendingQualifiers)) {
      showModal(GENERAL_MODAL, {
        component: fm(messages.saveWithParticipants),
        onConfirm: () => onSubmit(true),
        confirmText: fm(messages.confirmWithParticipants),
        showCancel: true
      })
      return
    }

    if (savePending) { return }
    setSavePending(true)

    const nextGoal = pendingEdits.set('goalParams', pendingParams.set('qualifiers', pendingQualifiers))

    updateGoal(goal.get('id'), { goal: nextGoal.toJS() })
      .then(() => {
        if (force && participants.size > 0 && needsConfirmChanges(pendingEdits, pendingParams, pendingQualifiers)) {
          forceRefetch()
        }
      })
      .then(() => {
        setPendingEdits(Map())
        setPendingParams(Map())
        setPendingQualifiers(Map())
      })
      .finally(() => {
        setSavePending(false)
      })
  }

  // HACK: rails Date is served to us as 'YYYY-MM-DD', which js Date doesn't
  // know what to do with, resulting in this terrible mess
  // new Date('2021-01-18').getDate() // => 17, and getDate is called inside <DayPicker />,
  // so we have to do this fake timezone nonsense
  const getDate = str => {
    return isNaN(new Date(str + 'T00:00:00').getDate())
      ? new Date(str)
      : new Date(str + 'T00:00:00')
  }

  const configureProps = { goal, values, setPendingEdit, params, setParam, metricUnits, fm }
  const saveDisabled = (pendingEdits.size + pendingParams.size + pendingQualifiers.size) === 0
  return (
    <div className={styles.gutter}>
      <SectionBox noPadding title={fm(featureNames.settings)}>
        <form onSubmit={() => onSubmit()}>
          <div className={styles.section}>
            <div className={styles.dates}>
              <div>
                <label>{fm(messages.starts)}</label>
                <DatePickerInput date={getDate(values.startsOn)} onDateChange={date => setPendingEdit('startsOn', date.toISOString())} />
              </div>
              <span className={styles.startToEnd}>{fm(messages.to)}</span>
              <div>
                <label>{fm(messages.ends)}</label>
                <DatePickerInput date={getDate(values.endsOn)} onDateChange={date => setPendingEdit('endsOn', date.toISOString())} />
              </div>
            </div>
            <div className={styles.tagInput}>
              <label>{fm(messages.tagLabel)}</label>
              <input
                type='text'
                value={qualifiers.get('tag', '')}
                onChange={onTagChange}
                placeholder={fm(messages.tagPlaceholder)}
              />
            </div>
            <div>
              <label>{fm(messages.allowManual)}</label>
              <RadioGroup
                style={{ margin: '0 0' }}
                horizontal
                inline
                options={allowManualOpts(fm)}
                selected={qualifiers.get('manual', false).toString()}
                onChange={e => setQualifier('manual', e === 'true')} />
            </div>
          </div>
          <hr />
          <div className={styles.section}>
            <div>
              <label>{fm(messages.participationType)}</label>
              <BlockButtonGroup
                options={ptMap(fm)}
                selected={params.get('participation_type')}
                onChange={e => setParam('participation_type', e)} />
            </div>
            <div>
              <label>{fm(messages.challengeCriteria)}</label>
              <BlockButtonGroup
                className={styles.greyButtons}
                options={critMap(fm)}
                selected={values.get('goalType')}
                onChange={onGoalTypeChange} />
            </div>
            {values.goalType === 'distance' && <ConfigureDistance {...configureProps} />}
            {values.goalType === 'elevation_gain' && <ConfigureElevation {...configureProps} />}
            {values.goalType === 'moving_time' && <ConfigureTime {...configureProps} />}
          </div>
          <hr />
          <div className={cx(styles.section, styles.ctasSection)}>
            <CTA primary onClick={() => onSubmit()} disabled={saveDisabled || savePending}>
              {savePending && <>
                <CircleSpinner size={14} />
                <span style={{ marginLeft: '10px' }}>{fm(commonMessages.saving)}</span>
              </>}
              {!savePending && <>
                {saveDisabled
                  ? fm(commonMessages.changesSaved)
                  : _.startCase(fm(messages.saveChanges))}
              </>}
            </CTA>
            <CTA link href={`/challenges/${goal.id}/overview`}>
              {fm(commonCTAs.cancel)}
            </CTA>
          </div>
        </form>
      </SectionBox>
      <section ref={milestonesRef}>
        <MilestoneConfig
          goal={goal}
          goalType={values.get('goalType')}
          goalParams={params}
          disabled={milestonesBlocking(pendingEdits, pendingParams)}
          fm={fm}
          metricUnits={metricUnits}
        />
      </section>
    </div>
  )
}

const distanceTargets = fm => OrderedMap({
  fixed: fm(messages.fixedDistance),
  route: fm(messages.routeDistance),
  none: fm(messages.noTarget)
})

const _ConfigureDistance = ({ route, fetchRoute, goal, values, setPendingEdit, params, setParam, metricUnits, fm }) => {
  const unitMessage = metricUnits ? commonUnits.kilometers : commonUnits.miles
  const [target, setTarget] = useState(undefined)
  useEffect(() => {
    if (goal.get('goalParams').max) {
      setTarget('fixed')
    } else if (goal.get('goalParams').route_id) {
      setTarget('route')
    } else {
      setTarget('none')
    }
  }, [])

  const loadRoute = async routeId => {
    await fetchRoute(routeId)
    setParam('route_id', routeId)
  }

  const chooseRoute = () => showModal(LOAD_EXISTING_MODAL, {
    loadRoute,
    routesOnly: true,
    allowedResultTypes: [resultTypes.USER],
    submitLabel: fm(messages.addToChallenge),
    preventMultiselect: true
  })

  const onTargetChange = e => {
    if (e === 'fixed') {
      setParam({
        has_target: true,
        route_id: null
      })
    }
    if (e === 'route') {
      setParam(Object.assign(
        {
          'has_target': true,
          max: null
        },
        goal.get('goalParams').route_id && {
          route_id: goal.get('goalParams').route_id
        }
      ))
    }
    if (e === 'none') {
      setParam({
        'has_target': false,
        route_id: null,
        max: null
      })
    }
    setTarget(e)
  }

  const onChange = e => {
    const v = parseInt(e, 10)
    setParam('max', metricUnits ? v * METERS_IN_KM : v * METERS_IN_MILE) // gotta store in meters
  }

  return <>
    <div>
      <div style={{ marginBottom: '30px' }}>
        {fm(messages.distanceDesc, {
          unit: fm(unitMessage, { count: 1 }),
          units: fm(unitMessage, { count: 0 }),
          a: (...c) => <a href='/help/challenges-for-organizations#criteria'>{c}</a>
        })}
      </div>
      <label>{fm(messages.completionTarget)}</label>
      <RadioGroup
        style={{ margin: '0 0' }}
        horizontal
        inline
        options={distanceTargets(fm)}
        selected={target}
        onChange={onTargetChange} />
    </div>
    {target === 'fixed' && <div className={styles.inlineLabel}>
      <span>{fm(messages.setDistance)}</span>
      <Distance value={params.get('max')}>
        {m => <InteriorLabelInput
          noMargin
          className={styles.fixedInput}
          label={fm(metricUnits ? unitMessages.kmShort : unitMessages.miShort)}
          defaultValue={Math.floor(m.value)}
          onChange={onChange} />}
      </Distance>
    </div>}
    {target === 'route' && <div style={{ display: 'flex' }}>
      {params.get('route_id')
        ? <RoutePreview
          replace={chooseRoute}
          remove={() => setParam('route_id', null)}
          route={route(params.get('route_id'))}
          fm={fm}
        />
        : <CTA onClick={chooseRoute} secondary style={{ width: 'auto' }}>
          <Icon i='md-pin' style={{ marginRight: '15px' }} />{fm(messages.selectARoute)}
        </CTA>}
    </div>}
  </>
}

const ConfigureDistance = connect(
  (state, ownProps) => ({ route: id => routeSelectors.find(state, id) }),
  dispatch => ({ fetchRoute: id => dispatch(routeActions.read(id)) })
)(_ConfigureDistance)

const RoutePreview = ({ route, replace, remove, fm }) => {
  return <div className={styles.routePreview}>
    <RouteListItem
      noBadge
      route={route}
    />
    <div className={styles.ctasSection}>
      <CTA onClick={replace} link>{c(fm(messages.replace))}</CTA>
      <CTA onClick={remove} link>{c(fm(commonVerbs.remove))}</CTA>
    </div>
  </div>
}

const elevationTargets = fm => OrderedMap({
  fixed: fm(messages.fixedElevation),
  none: fm(messages.noTarget)
})

const ConfigureElevation = ({ goal, values, setPendingEdit, params, setParam, metricUnits, fm }) => {
  const unitMessage = metricUnits ? unitMessages.mShort : unitMessages.ftShort
  const [target, setTarget] = useState('fixed')

  const onTargetChange = e => {
    if (e === 'none') {
      setParam('max', null)
    }
    if (e === 'fixed' && !params.get('max') && goal.goalParams['max']) {
      setParam('max', goal.goalParams['max'])
    }
    setTarget(e)
  }

  const onChange = e => {
    const v = parseInt(e, 10)
    setParam('max', metricUnits ? v : v * METERS_IN_FOOT) // gotta store in meters
  }

  return <div>
    <div>
      <div style={{ marginBottom: '30px' }}>
        {fm(messages.elevationDesc, {
          unit: fm(unitMessage, { count: 1 }),
          a: (...c) => <a href='/help/challenges-for-organizations#criteria'>{c}</a>
        })}
      </div>
      <label>{fm(messages.completionTarget)}</label>
      <RadioGroup
        style={{ margin: '0 0' }}
        horizontal
        inline
        options={elevationTargets(fm)}
        selected={target}
        onChange={onTargetChange} />
    </div>
    {target === 'fixed' && <div className={styles.inlineLabel}>
      <span>{fm(messages.setElevation)}</span>
      <Elevation value={params.get('max')}>
        {m => <InteriorLabelInput
          noMargin
          className={styles.fixedInput}
          label={fm(metricUnits ? unitMessages.mShort : unitMessages.ftShort)}
          defaultValue={Math.round(m.value)}
          onChange={onChange} />}
      </Elevation>
    </div>}
  </div>
}

const timeTargets = fm => OrderedMap({
  fixed: fm(messages.fixedTime),
  none: fm(messages.noTarget)
})

const ConfigureTime = ({ goal, values, setPendingEdit, params, setParam, fm }) => {
  const [target, setTarget] = useState('fixed')

  const onTargetChange = e => {
    if (e === 'none') {
      setParam('max', null)
    }
    if (e === 'fixed' && !params.get('max') && goal.goalParams['max']) {
      setParam('max', goal.goalParams['max']) // store as seconds
    }
    setTarget(e)
  }

  return <div>
    <div>
      <div style={{ marginBottom: '30px' }}>
        {fm(messages.timeDesc, {
          a: (...c) => <a href='/help/challenges-for-organizations#criteria'>{c}</a>
        })}
      </div>
      <label>{fm(messages.completionTarget)}</label>
      <RadioGroup
        style={{ margin: '0 0' }}
        horizontal
        inline
        options={timeTargets(fm)}
        selected={target}
        onChange={onTargetChange} />
    </div>
    {target === 'fixed' && <div className={styles.inlineLabel}>
      <span>{fm(messages.setTime)}</span>
      <InteriorLabelInput
        noMargin
        className={styles.fixedInput}
        label={fm(unitMessages.hrsShort)}
        value={Math.round(params.get('max') / 3600)} // display as hours
        onChange={v => setParam('max', parseInt(v, 10) * 3600)} // store as seconds
      />
    </div>
    }
  </div>
}
